import {
  Mail,
  Calendar,
  MessageCircle,
  FolderOpen,
  PenTool,
  Video,
  BarChart3,
  CreditCard,
  Layers,
} from "lucide-react"
import type { CategoryTab, Integration } from "@/types/integrations.types"

export const CATEGORIES: CategoryTab[] = [
  { id: "All", label: "All", icon: Layers },
  { id: "Email", label: "Email", icon: Mail },
  { id: "Calendar", label: "Calendar", icon: Calendar },
  { id: "Messaging", label: "Messaging", icon: MessageCircle },
  { id: "Storage", label: "Storage", icon: FolderOpen },
  { id: "E-Signature", label: "E-Signature", icon: PenTool },
  { id: "Meetings", label: "Meetings", icon: Video },
  { id: "CRM", label: "CRM", icon: BarChart3 },
  { id: "Billing", label: "Billing", icon: CreditCard },
]

export const INTEGRATIONS: Integration[] = [
  {
    id: "gmail",
    name: "Gmail",
    description: "Send and receive email from your Gmail account",
    category: "Email",
    scope: "personal",
    status: "connected",
    provider: "via Nylas",
    icon: Mail,
    accentFrom: "from-red-500/20",
    accentTo: "to-orange-500/20",
    accentBorder: "border-red-500/30",
  },
  {
    id: "outlook-email",
    name: "Outlook",
    description: "Connect your Outlook email for deal communication",
    category: "Email",
    scope: "personal",
    status: "available",
    provider: "via Nylas",
    icon: Mail,
    accentFrom: "from-blue-500/20",
    accentTo: "to-cyan-500/20",
    accentBorder: "border-blue-500/30",
  },
  {
    id: "google-calendar",
    name: "Google Calendar",
    description: "Sync meetings and deadlines from Google Calendar",
    category: "Calendar",
    scope: "personal",
    status: "connected",
    provider: "via Nylas",
    icon: Calendar,
    accentFrom: "from-blue-500/20",
    accentTo: "to-green-500/20",
    accentBorder: "border-blue-500/30",
  },
  {
    id: "outlook-calendar",
    name: "Outlook Calendar",
    description: "Keep your Outlook calendar in sync with deal timelines",
    category: "Calendar",
    scope: "personal",
    status: "available",
    provider: "via Nylas",
    icon: Calendar,
    accentFrom: "from-blue-600/20",
    accentTo: "to-indigo-500/20",
    accentBorder: "border-blue-600/30",
  },
  {
    id: "slack",
    name: "Slack",
    description: "Get deal updates and notifications in Slack channels",
    category: "Messaging",
    scope: "organization",
    status: "available",
    icon: MessageCircle,
    accentFrom: "from-purple-500/20",
    accentTo: "to-fuchsia-500/20",
    accentBorder: "border-purple-500/30",
  },
  {
    id: "whatsapp",
    name: "WhatsApp",
    description: "Communicate with contacts through WhatsApp",
    category: "Messaging",
    scope: "organization",
    status: "coming_soon",
    icon: MessageCircle,
    accentFrom: "from-green-500/20",
    accentTo: "to-emerald-500/20",
    accentBorder: "border-green-500/30",
  },
  {
    id: "linkedin",
    name: "LinkedIn",
    description: "Import contacts and communicate via LinkedIn messaging",
    category: "Messaging",
    scope: "organization",
    status: "coming_soon",
    icon: MessageCircle,
    accentFrom: "from-blue-600/20",
    accentTo: "to-blue-400/20",
    accentBorder: "border-blue-600/30",
  },
  {
    id: "ms-teams",
    name: "Microsoft Teams",
    description: "Collaborate on deals through Microsoft Teams",
    category: "Messaging",
    scope: "organization",
    status: "coming_soon",
    icon: MessageCircle,
    accentFrom: "from-indigo-500/20",
    accentTo: "to-violet-500/20",
    accentBorder: "border-indigo-500/30",
  },
  {
    id: "google-drive",
    name: "Google Drive",
    description: "Store and access deal documents in Google Drive",
    category: "Storage",
    scope: "organization",
    status: "available",
    icon: FolderOpen,
    accentFrom: "from-yellow-500/20",
    accentTo: "to-green-500/20",
    accentBorder: "border-yellow-500/30",
  },
  {
    id: "dropbox",
    name: "Dropbox",
    description: "Sync deal files and contracts with Dropbox",
    category: "Storage",
    scope: "organization",
    status: "available",
    icon: FolderOpen,
    accentFrom: "from-blue-500/20",
    accentTo: "to-blue-300/20",
    accentBorder: "border-blue-500/30",
  },
  {
    id: "onedrive",
    name: "OneDrive",
    description: "Access and manage deal documents in OneDrive",
    category: "Storage",
    scope: "organization",
    status: "coming_soon",
    icon: FolderOpen,
    accentFrom: "from-sky-500/20",
    accentTo: "to-blue-500/20",
    accentBorder: "border-sky-500/30",
  },
  {
    id: "box",
    name: "Box",
    description: "Enterprise content management for deal documents",
    category: "Storage",
    scope: "organization",
    status: "coming_soon",
    icon: FolderOpen,
    accentFrom: "from-blue-400/20",
    accentTo: "to-cyan-400/20",
    accentBorder: "border-blue-400/30",
  },
  {
    id: "docusign",
    name: "DocuSign",
    description: "Send and track contracts for electronic signature",
    category: "E-Signature",
    scope: "organization",
    status: "available",
    icon: PenTool,
    accentFrom: "from-yellow-500/20",
    accentTo: "to-amber-500/20",
    accentBorder: "border-yellow-500/30",
  },
  {
    id: "adobe-sign",
    name: "Adobe Sign",
    description: "Collect signatures with Adobe Acrobat Sign",
    category: "E-Signature",
    scope: "organization",
    status: "coming_soon",
    icon: PenTool,
    accentFrom: "from-red-500/20",
    accentTo: "to-rose-500/20",
    accentBorder: "border-red-500/30",
  },
  {
    id: "dropbox-sign",
    name: "Dropbox Sign",
    description: "E-signatures powered by Dropbox Sign (HelloSign)",
    category: "E-Signature",
    scope: "organization",
    status: "coming_soon",
    icon: PenTool,
    accentFrom: "from-blue-500/20",
    accentTo: "to-indigo-500/20",
    accentBorder: "border-blue-500/30",
  },
  {
    id: "meeting-bot",
    name: "Meeting Bot",
    description: "AI-powered meeting transcription and deal extraction",
    category: "Meetings",
    scope: "personal",
    status: "connected",
    icon: Video,
    accentFrom: "from-violet-500/20",
    accentTo: "to-purple-500/20",
    accentBorder: "border-violet-500/30",
  },
  {
    id: "zoom",
    name: "Zoom",
    description: "Join and record deal meetings on Zoom",
    category: "Meetings",
    scope: "organization",
    status: "available",
    icon: Video,
    accentFrom: "from-blue-500/20",
    accentTo: "to-sky-500/20",
    accentBorder: "border-blue-500/30",
  },
  {
    id: "google-meet",
    name: "Google Meet",
    description: "Schedule and join meetings via Google Meet",
    category: "Meetings",
    scope: "organization",
    status: "coming_soon",
    icon: Video,
    accentFrom: "from-green-500/20",
    accentTo: "to-teal-500/20",
    accentBorder: "border-green-500/30",
  },
  {
    id: "salesforce",
    name: "Salesforce",
    description: "Sync deals, contacts, and pipeline with Salesforce CRM",
    category: "CRM",
    scope: "organization",
    status: "coming_soon",
    icon: BarChart3,
    accentFrom: "from-sky-500/20",
    accentTo: "to-blue-600/20",
    accentBorder: "border-sky-500/30",
  },
  {
    id: "hubspot",
    name: "HubSpot",
    description: "Connect your HubSpot CRM for deal and contact sync",
    category: "CRM",
    scope: "organization",
    status: "coming_soon",
    icon: BarChart3,
    accentFrom: "from-orange-500/20",
    accentTo: "to-red-500/20",
    accentBorder: "border-orange-500/30",
  },
  {
    id: "stripe",
    name: "Stripe",
    description: "Track payments, invoices, and billing for your deals",
    category: "Billing",
    scope: "organization",
    status: "available",
    icon: CreditCard,
    accentFrom: "from-violet-500/20",
    accentTo: "to-indigo-500/20",
    accentBorder: "border-violet-500/30",
  },
  {
    id: "quickbooks",
    name: "QuickBooks",
    description: "Sync invoices and financial data with QuickBooks",
    category: "Billing",
    scope: "organization",
    status: "coming_soon",
    icon: CreditCard,
    accentFrom: "from-green-500/20",
    accentTo: "to-emerald-500/20",
    accentBorder: "border-green-500/30",
  },
]
